import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatPoints } from "@/lib/format";
import { Trash2, Coins, Network, Search, Shield, Archive, RotateCcw, AlertTriangle, MailCheck, MailX, Send, KeyRound, Crown, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";
import ReferralTreeDialog from "./ReferralTreeDialog";

export default function AdminUsersTab({ onChange }) {
    const [users, setUsers] = useState([]);
    const [deletedUsers, setDeletedUsers] = useState([]);
    const [view, setView] = useState("active"); // "active" | "deleted"
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [refTree, setRefTree] = useState(null);
    const [refOpen, setRefOpen] = useState(false);

    const load = useCallback(async (search) => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/users", { params: { q: search || undefined } });
            setUsers(data);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadDeleted = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/users/deleted");
            setDeletedUsers(data);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(""); }, [load]);

    useEffect(() => {
        if (view === "deleted") {
            loadDeleted();
        } else {
            const t = setTimeout(() => load(q), 300);
            return () => clearTimeout(t);
        }
    }, [q, view, load, loadDeleted]);

    const restoreUser = async (userId, username) => {
        if (!window.confirm(`¿Restaurar a @${username}? Podrá volver a acceder a la plataforma.`)) return;
        try {
            await api.post(`/admin/users/${userId}/restore`);
            toast.success(`@${username} restaurado`);
            await loadDeleted();
            await load(q);
            onChange?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al restaurar");
        }
    };

    const purgeUser = async (userId, username) => {
        if (!window.confirm(`⚠️ BORRAR PERMANENTEMENTE a @${username}? Esta acción no se puede revertir.`)) return;
        try {
            await api.delete(`/admin/users/${userId}/purge`);
            toast.success(`@${username} borrado definitivamente`);
            await loadDeleted();
            onChange?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al borrar");
        }
    };

    const openReferrals = async (userId) => {
        try {
            const { data } = await api.get(`/admin/users/${userId}/referrals`);
            setRefTree(data);
            setRefOpen(true);
        } catch (e) {
            toast.error("No se pudo cargar el árbol");
        }
    };

    return (
        <div>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
                <button
                    onClick={() => setView("active")}
                    data-testid="users-tab-active"
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider border transition flex items-center gap-1.5 ${
                        view === "active"
                            ? "bg-p2w-green text-p2w-green-fg border-p2w-green"
                            : "bg-p2w-surface text-white/80 border-p2w-line hover:border-p2w-green/40"
                    }`}
                >
                    Activos <span className="text-[10px] opacity-70">({users.length})</span>
                </button>
                <button
                    onClick={() => setView("deleted")}
                    data-testid="users-tab-deleted"
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider border transition flex items-center gap-1.5 ${
                        view === "deleted"
                            ? "bg-p2w-amber text-black border-p2w-amber"
                            : "bg-p2w-surface text-white/80 border-p2w-line hover:border-p2w-amber/40"
                    }`}
                >
                    <Archive className="w-3 h-3" /> Eliminados <span className="text-[10px] opacity-70">({deletedUsers.length})</span>
                </button>
                {view === "active" && (
                    <div className="relative ml-auto w-full max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-p2w-gray" />
                        <Input
                            data-testid="admin-users-search"
                            placeholder="Buscar por email o username…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="pl-9 bg-p2w-surface border-p2w-line text-white focus-visible:ring-p2w-green/40"
                        />
                    </div>
                )}
            </div>

            {view === "deleted" && (
                <div className="mb-4 rounded-xl border border-p2w-amber/30 bg-p2w-amber/5 p-3 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-p2w-amber mt-0.5 shrink-0" />
                    <p className="text-xs text-p2w-amber">
                        Los usuarios eliminados se conservan <strong>8 días</strong> antes de ser borrados definitivamente. Mientras tanto puedes restaurarlos sin pérdida de datos.
                    </p>
                </div>
            )}

            <div className="overflow-x-auto rounded-2xl border border-p2w-line bg-p2w-surface">
                {view === "active" ? (
                    <table className="w-full text-sm">
                        <thead className="bg-p2w-elevated border-b border-p2w-line text-p2w-gray text-xs uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-4 py-3">Usuario</th>
                                <th className="text-left px-4 py-3">Rol</th>
                                <th className="text-left px-4 py-3">Código ref.</th>
                                <th className="text-right px-4 py-3">Referidos L1</th>
                                <th className="text-right px-4 py-3">Saldo</th>
                                <th className="text-right px-4 py-3">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (<tr><td colSpan={6} className="text-center text-p2w-gray py-10">Cargando…</td></tr>)}
                            {!loading && users.map((u) => (
                                <UserRow key={u.id} u={u} onReload={() => { load(q); onChange?.(); }} onOpenRefs={() => openReferrals(u.id)} />
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-p2w-elevated border-b border-p2w-line text-p2w-gray text-xs uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-4 py-3">Usuario</th>
                                <th className="text-left px-4 py-3">Eliminado el</th>
                                <th className="text-right px-4 py-3">Días para purga</th>
                                <th className="text-right px-4 py-3">Saldo</th>
                                <th className="text-right px-4 py-3">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (<tr><td colSpan={5} className="text-center text-p2w-gray py-10">Cargando…</td></tr>)}
                            {!loading && deletedUsers.length === 0 && (
                                <tr><td colSpan={5} className="text-center text-p2w-gray py-10">Sin usuarios eliminados.</td></tr>
                            )}
                            {!loading && deletedUsers.map((u) => (
                                <DeletedUserRow key={u.id} u={u} onRestore={() => restoreUser(u.id, u.username)} onPurge={() => purgeUser(u.id, u.username)} />
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            <ReferralTreeDialog open={refOpen} onOpenChange={setRefOpen} data={refTree} />
        </div>
    );
}

function DeletedUserRow({ u, onRestore, onPurge }) {
    const daysLeft = u.days_until_purge ?? 0;
    const urgent = daysLeft <= 2;
    return (
        <tr className="border-t border-p2w-line" data-testid={`deleted-user-row-${u.username}`}>
            <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                    <Avatar className="w-8 h-8 opacity-60">
                        <AvatarImage src={u.avatar_url} />
                        <AvatarFallback className="bg-p2w-gray/30 text-white text-xs">
                            {(u.username || "?").slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                    </Avatar>
                    <div>
                        <div className="text-white font-medium opacity-80">@{u.username}</div>
                        <div className="text-xs text-p2w-gray">{u.email}</div>
                    </div>
                </div>
            </td>
            <td className="px-4 py-3 text-xs text-p2w-gray">
                {u.deleted_at ? new Date(u.deleted_at).toLocaleString() : "—"}
            </td>
            <td className="px-4 py-3 text-right">
                <span className={`font-sora font-bold number-tabular ${urgent ? "text-p2w-red" : "text-p2w-amber"}`}>
                    {daysLeft} {daysLeft === 1 ? "día" : "días"}
                </span>
            </td>
            <td className="px-4 py-3 text-right font-sora text-p2w-gray number-tabular">
                {formatPoints(u.points_balance || 0)}
            </td>
            <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-2">
                    <Button
                        size="sm"
                        onClick={onRestore}
                        data-testid={`restore-user-${u.username}`}
                        className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold h-8 gap-1.5"
                    >
                        <RotateCcw className="w-3.5 h-3.5" /> Restaurar
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={onPurge}
                        data-testid={`purge-user-${u.username}`}
                        title="Borrar permanentemente"
                        className="border-p2w-red/40 bg-p2w-red/10 text-p2w-red hover:bg-p2w-red/20 hover:text-p2w-red h-8 px-3"
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                </div>
            </td>
        </tr>
    );
}

function UserRow({ u, onReload, onOpenRefs }) {
    const { user: me } = useAuth();
    const isSelf = me && me !== false && me.id === u.id;
    return (
        <tr className="border-t border-p2w-line" data-testid={`admin-user-row-${u.username}`}>
            <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                    <Avatar className="w-8 h-8">
                        <AvatarImage src={u.avatar_url} />
                        <AvatarFallback className="bg-p2w-purple text-white text-xs">
                            {(u.username || "?").slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                    </Avatar>
                    <div>
                        <div className="text-white font-medium flex items-center gap-1.5">
                            @{u.username}
                            {isSelf && <span className="text-[9px] uppercase tracking-wider text-p2w-gray">(tú)</span>}
                            {u.email_verified ? (
                                <span
                                    title={u.email_verified_manually ? "Verificado manualmente por admin" : "Email verificado"}
                                    className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider text-p2w-green"
                                    data-testid={`user-verified-${u.username}`}
                                >
                                    <MailCheck className="w-3 h-3" />
                                    {u.email_verified_manually && <span className="opacity-60">manual</span>}
                                </span>
                            ) : (
                                <span
                                    title="Email no verificado"
                                    className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider text-p2w-amber"
                                    data-testid={`user-unverified-${u.username}`}
                                >
                                    <MailX className="w-3 h-3" /> sin verificar
                                </span>
                            )}
                        </div>
                        <div className="text-xs text-p2w-gray">{u.email}</div>
                    </div>
                </div>
            </td>
            <td className="px-4 py-3">
                {u.role === "admin" ? (
                    <Badge className="bg-p2w-purple/20 text-p2w-purple border border-p2w-purple/40 text-[10px] uppercase tracking-wider">Admin</Badge>
                ) : u.is_creator ? (
                    <Badge className="bg-p2w-magenta/15 text-p2w-magenta border border-p2w-magenta/40 text-[10px] uppercase tracking-wider">Creador</Badge>
                ) : (
                    <span className="text-xs text-p2w-gray">user</span>
                )}
            </td>
            <td className="px-4 py-3">
                <code className="px-2 py-0.5 rounded bg-p2w-elevated border border-p2w-line text-p2w-green text-xs font-mono">
                    {u.referral_code || "—"}
                </code>
            </td>
            <td className="px-4 py-3 text-right text-white number-tabular">{u.direct_referrals}</td>
            <td className="px-4 py-3 text-right font-sora font-bold text-p2w-green number-tabular">
                {formatPoints(u.points_balance)}
            </td>
            <td className="px-4 py-3 text-right">
                <div className="flex items-center gap-2 justify-end flex-wrap">
                    {!u.email_verified && <VerifyEmailActions user={u} onDone={onReload} />}
                    <SendPasswordResetButton user={u} />
                    <Button size="sm" variant="outline" data-testid={`view-referrals-${u.username}`} onClick={onOpenRefs} className="border-p2w-line bg-p2w-elevated text-white hover:bg-p2w-line hover:text-p2w-green h-8">
                        <Network className="w-3.5 h-3.5 mr-1.5" /> Red
                    </Button>
                    {!u.is_creator && u.role !== "admin" && <MakeCreatorButton user={u} onDone={onReload} />}
                    <RoleDialog user={u} isSelf={isSelf} onDone={onReload} />
                    <AdjustBalanceDialog user={u} onDone={onReload} />
                    {!isSelf && u.role !== "admin" && <DeleteUserDialog user={u} onDone={onReload} />}
                </div>
            </td>
        </tr>
    );
}

function VerifyEmailActions({ user, onDone }) {
    const [busy, setBusy] = useState(null); // 'verify' | 'resend' | null

    const onVerify = async () => {
        if (!window.confirm(`Marcar manualmente el email de @${user.username} (${user.email}) como verificado?`)) return;
        setBusy("verify");
        try {
            await api.post(`/admin/users/${user.id}/verify-email`);
            toast.success(`@${user.username} verificado manualmente`);
            onDone?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al verificar");
        } finally {
            setBusy(null);
        }
    };

    const onResend = async () => {
        setBusy("resend");
        try {
            const { data } = await api.post(`/admin/users/${user.id}/resend-verification`);
            if (data.delivered) {
                toast.success(`Código reenviado a ${user.email}`);
            } else {
                toast.warning(`Código generado, pero el correo no se entregó (revisa Resend)`);
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al reenviar");
        } finally {
            setBusy(null);
        }
    };

    return (
        <>
            <Button
                size="sm"
                variant="outline"
                data-testid={`resend-verification-${user.username}`}
                onClick={onResend}
                disabled={busy !== null}
                title="Reenviar código de verificación al usuario"
                className="border-p2w-line bg-p2w-elevated text-white hover:bg-p2w-line hover:text-p2w-amber h-8"
            >
                <Send className="w-3.5 h-3.5 mr-1.5" />
                {busy === "resend" ? "Enviando…" : "Reenviar"}
            </Button>
            <Button
                size="sm"
                variant="outline"
                data-testid={`mark-verified-${user.username}`}
                onClick={onVerify}
                disabled={busy !== null}
                title="Marcar email como verificado sin enviar correo"
                className="border-p2w-green/40 bg-p2w-green/5 text-p2w-green hover:bg-p2w-green/15 h-8"
            >
                <MailCheck className="w-3.5 h-3.5 mr-1.5" />
                {busy === "verify" ? "…" : "Verificar"}
            </Button>
        </>
    );
}

function SendPasswordResetButton({ user }) {
    const [busy, setBusy] = useState(false);

    const onClick = async () => {
        if (!window.confirm(`Enviar correo de recuperación de contraseña a @${user.username} (${user.email})?\n\nRecibirá un enlace válido por 1 hora para crear una nueva contraseña.`)) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/users/${user.id}/send-password-reset`);
            if (data.delivered) {
                toast.success(`Enlace de recuperación enviado a ${user.email}`);
            } else {
                toast.warning(`Enlace generado, pero el correo no se entregó (revisa Resend / dominio)`);
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al enviar recuperación");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Button
            size="sm"
            variant="outline"
            data-testid={`send-password-reset-${user.username}`}
            onClick={onClick}
            disabled={busy}
            title="Enviar correo para recuperar la contraseña"
            className="border-p2w-purple/40 bg-p2w-purple/5 text-p2w-purple hover:bg-p2w-purple/15 h-8"
        >
            <KeyRound className="w-3.5 h-3.5 mr-1.5" />
            {busy ? "Enviando…" : "Recuperar"}
        </Button>
    );
}

function MakeCreatorButton({ user, onDone }) {
    const [open, setOpen] = useState(false);
    const [handle, setHandle] = useState(user.username || "");
    const [tier, setTier] = useState("bronze");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleOpenChange = (v) => {
        setOpen(v);
        if (!v) {
            // Reset on close to avoid stale state on reopen
            setHandle(user.username || "");
            setTier("bronze");
            setNotes("");
        }
    };

    const handleNormalized = handle.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");

    const submit = async () => {
        if (!handleNormalized || handleNormalized.length < 2) {
            toast.error("Handle inválido (mín 2 caracteres)");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/creators/grant", {
                user_id: user.id,
                handle: handleNormalized,
                tier,
                notes: notes.trim() || null,
            });
            toast.success(`@${data.username} acreditado como creador en /c/${data.handle}`);
            setOpen(false);
            onDone?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo acreditar");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
                <Button
                    size="sm"
                    variant="outline"
                    data-testid={`make-creator-${user.username}`}
                    title="Acreditar como creador (bypass de requisitos)"
                    className="border-p2w-magenta/40 bg-p2w-magenta/5 text-p2w-magenta hover:bg-p2w-magenta/15 h-8"
                >
                    <Crown className="w-3.5 h-3.5 mr-1.5" /> Creator
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-p2w-surface border-p2w-line text-white max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-sora flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-p2w-magenta" />
                        Acreditar como creador
                    </DialogTitle>
                    <DialogDescription className="text-p2w-gray text-xs">
                        Promueve a este usuario al rol Creator sin requisitos. Queda en auditoría.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="rounded-xl bg-p2w-elevated border border-p2w-line p-3 flex items-center gap-3">
                        <Avatar className="w-10 h-10 ring-1 ring-p2w-magenta/30">
                            <AvatarImage src={user.avatar_url} />
                            <AvatarFallback className="bg-p2w-magenta text-white font-bold">
                                {(user.username || "?").slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                            <div className="text-white font-semibold truncate">@{user.username}</div>
                            <div className="text-xs text-p2w-gray truncate">{user.email}</div>
                        </div>
                    </div>

                    <div>
                        <Label className="text-[10px] uppercase tracking-wider text-p2w-gray font-semibold">
                            Handle público
                        </Label>
                        <div className="mt-1.5 flex items-center rounded-lg bg-p2w-elevated border border-p2w-line overflow-hidden">
                            <span className="px-3 text-p2w-gray font-mono text-sm">/c/</span>
                            <Input
                                value={handle}
                                onChange={(e) => setHandle(e.target.value)}
                                placeholder="handle"
                                className="bg-transparent border-0 text-white font-mono focus-visible:ring-0"
                                data-testid={`make-creator-handle-${user.username}`}
                            />
                        </div>
                        {handleNormalized && handleNormalized !== handle && (
                            <p className="text-[10px] text-p2w-gray mt-1">
                                Se guardará como: <span className="text-p2w-green font-mono">/c/{handleNormalized}</span>
                            </p>
                        )}
                    </div>

                    <div>
                        <Label className="text-[10px] uppercase tracking-wider text-p2w-gray font-semibold">
                            Tier inicial
                        </Label>
                        <div className="mt-1.5 grid grid-cols-3 gap-2">
                            {["bronze", "silver", "gold"].map((t) => (
                                <button
                                    key={t}
                                    onClick={() => setTier(t)}
                                    data-testid={`make-creator-tier-${t}-${user.username}`}
                                    className={`px-3 py-2 rounded-lg border text-xs font-bold uppercase tracking-wider transition ${
                                        tier === t
                                            ? "bg-p2w-magenta/20 border-p2w-magenta text-p2w-magenta"
                                            : "bg-p2w-elevated border-p2w-line text-p2w-gray hover:text-white"
                                    }`}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <Label className="text-[10px] uppercase tracking-wider text-p2w-gray font-semibold">
                            Nota interna (opcional)
                        </Label>
                        <Input
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Razón de la acreditación…"
                            className="bg-p2w-elevated border-p2w-line text-white mt-1.5"
                            data-testid={`make-creator-notes-${user.username}`}
                        />
                    </div>

                    <div className="rounded-lg bg-p2w-magenta/5 border border-p2w-magenta/30 p-3 text-xs text-p2w-gray">
                        Al confirmar, @{user.username} será promovido a <b className="text-p2w-magenta">Creator {tier}</b> y obtendrá el perfil <span className="font-mono text-p2w-green">/c/{handleNormalized || "..."}</span>. La acción queda en auditoría.
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} className="border-p2w-line bg-p2w-elevated text-white">
                        Cancelar
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={submitting || !handleNormalized || handleNormalized.length < 2}
                        className="bg-p2w-magenta text-white hover:bg-p2w-magenta/90 font-semibold"
                        data-testid={`make-creator-confirm-${user.username}`}
                    >
                        {submitting ? "Acreditando…" : "Acreditar"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function RoleDialog({ user, isSelf, onDone }) {
    const [open, setOpen] = useState(false);
    const [role, setRole] = useState(user.role || "user");
    const [isCreator, setIsCreator] = useState(!!user.is_creator);
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        setSubmitting(true);
        try {
            await api.patch(`/admin/users/${user.id}/role`, { role, is_creator: isCreator });
            toast.success(`Rol actualizado a ${role}`);
            setOpen(false);
            onDone?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    size="sm"
                    variant="outline"
                    data-testid={`edit-role-${user.username}`}
                    className={`h-8 ${user.role === "admin" ? "border-p2w-purple/40 bg-p2w-purple/10 text-p2w-purple hover:bg-p2w-purple/20 hover:text-p2w-purple" : "border-p2w-line bg-p2w-elevated text-white hover:bg-p2w-line hover:text-p2w-purple"}`}
                >
                    <Shield className="w-3.5 h-3.5 mr-1.5" /> Rol
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-p2w-surface border-p2w-line text-white">
                <DialogHeader>
                    <DialogTitle className="font-sora flex items-center gap-2">
                        <Shield className="w-5 h-5 text-p2w-purple" /> Editar rol · @{user.username}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    {isSelf && (
                        <div className="rounded-lg bg-p2w-amber/10 border border-p2w-amber/40 px-3 py-2 text-xs text-p2w-amber">
                            Eres tú. No puedes degradarte: si quieres quitarte el admin, pide a otro administrador.
                        </div>
                    )}
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-p2w-gray">Rol</label>
                        <Select value={role} onValueChange={setRole} disabled={isSelf}>
                            <SelectTrigger data-testid="role-select" className="mt-2 bg-p2w-elevated border-p2w-line text-white">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-p2w-elevated border-p2w-line text-white">
                                <SelectItem value="user">Usuario</SelectItem>
                                <SelectItem value="admin">Administrador</SelectItem>
                            </SelectContent>
                        </Select>
                        <p className="mt-2 text-xs text-p2w-gray">
                            {role === "admin"
                                ? "Tendrá acceso al panel /admin, podrá gestionar usuarios, retiros, categorías, mercados y configuración."
                                : "Solo podrá apostar, retirar y ver su perfil. Sin acceso al panel."}
                        </p>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            data-testid="role-is-creator"
                            checked={isCreator}
                            onChange={(e) => setIsCreator(e.target.checked)}
                            className="accent-p2w-green w-4 h-4"
                        />
                        <span className="text-white">Permitir crear mercados (creador)</span>
                    </label>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} className="border-p2w-line bg-p2w-elevated text-white hover:bg-p2w-line hover:text-white">
                        Cancelar
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={submitting}
                        data-testid="confirm-role"
                        className="bg-p2w-purple text-white hover:bg-p2w-purple/90 font-semibold"
                    >
                        Aplicar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function AdjustBalanceDialog({ user, onDone }) {
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState(0);
    const [reason, setReason] = useState("Crédito admin");
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        const n = Number(amount);
        if (!Number.isFinite(n) || n === 0) { toast.error("Cantidad inválida"); return; }
        setSubmitting(true);
        try {
            await api.post(`/admin/users/${user.id}/adjust-balance`, { amount: n, reason });
            toast.success("Saldo actualizado");
            setOpen(false);
            setAmount(0);
            onDone?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm" data-testid={`adjust-balance-${user.username}`} className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 h-8 font-semibold">
                    <Coins className="w-3.5 h-3.5 mr-1.5" /> Tokens
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-p2w-surface border-p2w-line text-white">
                <DialogHeader>
                    <DialogTitle className="font-sora">Ajustar tokens · @{user.username}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="px-4 py-3 rounded-lg bg-p2w-elevated border border-p2w-line flex items-center justify-between text-sm">
                        <span className="text-p2w-gray">Saldo actual</span>
                        <span className="font-sora font-bold text-p2w-green number-tabular text-lg">
                            {formatPoints(user.points_balance)} pts
                        </span>
                    </div>
                    <div>
                        <Label className="text-p2w-gray text-xs uppercase tracking-widest">Cantidad (+ acredita, - debita)</Label>
                        <Input type="number" data-testid="adjust-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-2 bg-p2w-elevated border-p2w-line text-white text-xl font-sora font-bold focus-visible:ring-p2w-green/40" />
                        <div className="mt-2 flex gap-2 flex-wrap">
                            {[-1000, -500, 500, 1000, 5000].map((v) => (
                                <button key={v} onClick={() => setAmount(v)} className={`text-xs px-3 py-1 rounded-md border ${v < 0 ? "border-p2w-red/40 text-p2w-red" : "border-p2w-green/40 text-p2w-green"} bg-p2w-elevated hover:bg-p2w-line transition`}>
                                    {v > 0 ? `+${v}` : v}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label className="text-p2w-gray text-xs uppercase tracking-widest">Motivo</Label>
                        <Input data-testid="adjust-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} className="border-p2w-line bg-p2w-elevated text-white hover:bg-p2w-line hover:text-white">Cancelar</Button>
                    <Button data-testid="confirm-adjust-balance" onClick={submit} disabled={submitting} className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold">
                        Aplicar ajuste
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function DeleteUserDialog({ user, onDone }) {
    return (
        <AlertDialog>
            <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" className="border-p2w-red/40 bg-p2w-red/10 text-p2w-red hover:bg-p2w-red/20 hover:text-p2w-red h-8" data-testid={`delete-user-${user.username}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-p2w-surface border-p2w-line text-white">
                <AlertDialogHeader>
                    <AlertDialogTitle className="font-sora">¿Eliminar a @{user.username}?</AlertDialogTitle>
                    <AlertDialogDescription className="text-p2w-gray">
                        El usuario se marcará como eliminado y no podrá iniciar sesión. Se conservará en la pestaña <strong className="text-white">Eliminados</strong> durante <strong className="text-p2w-amber">8 días</strong>, periodo en el que puedes restaurarlo si fue por error. Tras ese plazo se borrará definitivamente.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel className="bg-p2w-elevated border-p2w-line text-white hover:bg-p2w-line hover:text-white">Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                        className="bg-p2w-red text-white hover:bg-p2w-red/90"
                        onClick={async () => {
                            try {
                                await api.delete(`/admin/users/${user.id}`);
                                toast.success("Usuario eliminado (puedes restaurarlo en 8 días)");
                                onDone?.();
                            } catch (e) {
                                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
                            }
                        }}
                    >
                        Eliminar
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
